import PropTypes from "prop-types";
import { TableCellType } from "../table";

// Import individual cell components
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openModal, ToggleSwitch } from "risa-oasis-ui_v2";
import { DocWriteBackStatus } from "../../../../shared/enums/evBvWriteBackStatus";
import { ModalId } from "../../../../shared/enums/modalId";
import { setOpenedModalId } from "../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import ToolTipUsingPortal from "../../toolTipUsingPortal/toolTipUsingPortal";
import BadgeCell from "./cellTypes/BadgeCell";
import BadgeGroupCell from "./cellTypes/BadgeGroupCell";
import BadgeWithICell from "./cellTypes/BadgeWithICell";
import ButtonLinkCell from "./cellTypes/ButtonLinkCell";
import CheckboxCell from "./cellTypes/CheckboxCell";
import ClickableBadgeCell from "./cellTypes/ClickableBadgeCell";
import ClickableBadgeForFinalStatusCell from "./cellTypes/ClickableBadgeForFinalStatusCell";
import ClickableBadgeModalCell from "./cellTypes/ClickableBadgeModalCell";
import ClickableBadgeWithOnClickEmitter from "./cellTypes/ClickableBadgeWithOnClickEmitter";
import ClickableTextWithDisabledStatusCell from "./cellTypes/ClickableTextWithDisabledStatusCell";
import ColoredTextCell from "./cellTypes/ColoredTextCell";
import CopyDataCell from "./cellTypes/CopyDataCell";
import DateCell from "./cellTypes/DateCell";
import DeleteIconCell from "./cellTypes/DeleteIconCell";
import DocListCell from "./cellTypes/DocListCell";
import DocNameWithIcon from "./cellTypes/DocNameWithIcon";
import DocWithUploadIconCell from "./cellTypes/DocWithUploadIconCell";
import DownloadButtonCell from "./cellTypes/DownloadButtonCell";
import EditableTextCellComponent from "./cellTypes/EditableTextCell";
import EditDeleteIconsCell from "./cellTypes/EditDeleteIconsCell";
import EditIconCell from "./cellTypes/EditIconCell";
import ErrorModalCell from "./cellTypes/ErrorModalCell";
import IconCell from "./cellTypes/IconCell";
import InfoCell from "./cellTypes/InfoCell";
import MultilineCell from "./cellTypes/MultilineCell";
import NycbsStatusCell from "./cellTypes/NycbsStatusCell";
import RpaStatusCell from "./cellTypes/RpaStatusCell";
import { RpaStatusForExternalCell } from "./cellTypes/RpaStatusTwoCell";
import SearchableCardCell from "./cellTypes/SearchableCardCell";
import SelectableBadgeCell from "./cellTypes/SelectableBadgeCell";

import { STATUS_COLORS } from "../../../../shared/constants/statusColors";
import DocumentUploadIconWithBgColor from "../../../../shared/svg/documentUploadIconWithBgColor";
import { MedicalPaOrdersAuthStatus } from "../../../enums/medicalPaOrdersAuthStatus";
import IsDocumentUploaded from "./cellTypes/IsDocumentUploaded";
import { OverlappingIconsWithPopoverAndActionsCell } from "./cellTypes/OverlappingIconsWithPopoverAndActionsCell";
import { SideModalIconCell } from "./cellTypes/SideModalIconCell";
import StringBadgeCell from "./cellTypes/StringBadgeCell";
import StringCell from "./cellTypes/StringCell";
import StringWithButtonCell from "./cellTypes/StringWithButtonCell";
import StringWithCustomToolTipCell from "./cellTypes/StringWithCustomToolTipCell";
import StringWithIconCell from "./cellTypes/StringWithIconCell";
import TagWithIconCell from "./cellTypes/tagWithIconCell";
import TextCellWithInput from "./cellTypes/TextCellWithInput";
import ThinkingButtonCell from "./cellTypes/ThinkingButtonCell";
import UserInitialsCell from "./cellTypes/UserInitialsCell";
import ViewIconCell from "./cellTypes/ViewIconCell";
import ViewIconForScreenshotOrApiResponse from "./cellTypes/ViewIconForScreenshotOrApiResponse";

// TODO: File should be 150 lines or less, divide into smaller files

export const TableCell = (props) => {
  // const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);
  const { openedModalId } = useSelector((state: RootState) => state.modal);

  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const commentModalRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (openedModalId === ModalId.NAR_LETTER_MODAL) {
      openModal(ModalId.NAR_LETTER_MODAL);
    }
  }, [openedModalId]);

  useEffect(() => {
    if (openedModalId === ModalId.DOC_VIEWER_MODAL_WITH_TITLE) {
      openModal(ModalId.DOC_VIEWER_MODAL_WITH_TITLE);
    }
  }, [openedModalId]);

  switch (props.type) {
    case TableCellType.STRING:
      return <StringCell value={props.value} />;
    case TableCellType.BUTTON_LINK:
      return (
        <ButtonLinkCell
          value={props.value}
          onReviewClick={props.onReviewClick}
        />
      );
    case TableCellType.DATE:
      return <DateCell value={props.value} />;
    case TableCellType.DOWNLOAD_BUTTON:
      return (
        <DownloadButtonCell
          value={props.value}
          onDownloadButtonClick={props.handleDownloadButtonClick}
        />
      );
    case TableCellType.INFO:
      return <InfoCell value={props.value} />;
    case TableCellType.MULTILINE:
      return (
        <MultilineCell
          value={props.value}
          shouldShowLeftBorder={props.value?.shouldShowLeftBorder}
          leftBorderColor={props.value?.leftBorderColor}
        />
      );

    case TableCellType.TOGGLE_SWITCH: {
      const uniqueId =
        props.header != null && props.value?.id != null
          ? `${props.value.id}__${props.header}`
          : props.value?.id;
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <ToggleSwitch
            id={uniqueId}
            defaultChecked={props.value.defaultChecked}
            onChangeEmit={(data) =>
              props.onChangeEmit?.({
                ...data,
                name: props.value?.id ?? data.name,
                gridIdentifier: props.value?.gridIdentifier ?? props.header,
              })
            }
          />
        </div>
      );
    }
    case TableCellType.STRING_WITH_ICON:
      return <StringWithIconCell value={props.value} />;
    case TableCellType.COLORED_TEXT:
      return <ColoredTextCell value={props.value} />;
    case TableCellType.BADGE:
      return <BadgeCell value={props.value} />;
    case TableCellType.BADGE_WITH_I:
      return <BadgeWithICell value={props.value} />;
    case TableCellType.CLICKABLE_BADGE:
      return (
        <ClickableBadgeCell value={props.value} onComplete={props.onComplete} />
      );
    case TableCellType.CLICKABLE_BADGE_WITH_ON_CLICK:
      return (
        <ClickableBadgeWithOnClickEmitter
          value={props.value}
          onClick={props.onClickableBadgeClick}
        />
      );
    case TableCellType.CLICKABLE_BADGE_FOR_FINAL_STATUS:
      return (
        <ClickableBadgeForFinalStatusCell
          value={props.value}
          onComplete={props.onComplete}
        />
      );
    case TableCellType.CLICKABLE_BADGE_MODAL:
      return (
        <ClickableBadgeModalCell
          value={props.value}
          rowIndex={props.rowIndex}
          header={props.header}
          onTableRowUpdate={props.onTableRowUpdate}
          onValueChange={props.onValueChange}
        />
      );
    case TableCellType.ICON:
      return (
        <IconCell
          value={props.value}
          handleIconCellClick={props.handleIconCellClick}
        />
      );
    case TableCellType.STRING_BADGE:
      return <StringBadgeCell value={props.value} />;
    case TableCellType.ERROR_MODAL:
      return <ErrorModalCell value={props.value} />;
    case TableCellType.COPY_DATA:
      return <CopyDataCell value={props.value} />;
    case TableCellType.USER_INITIALS:
      return (
        <UserInitialsCell
          value={props.value}
          handleAssignToClick={props.handleAssignToClick}
        />
      );
    case TableCellType.NYCBS_STATUS:
      return (
        <NycbsStatusCell
          value={props.value}
          handleStatusClick={props.handleStatusClick}
        />
      );
    case TableCellType.DELETE_ICON:
      return (
        <DeleteIconCell
          value={props.value}
          handleDeleteIconClick={props.handleDeleteIconClick}
        />
      );
    case TableCellType.EDIT_ICON:
      return (
        <EditIconCell
          value={props.value}
          handleEditIconClick={props.handleEditIconClick}
        />
      );
    case TableCellType.CLICKABLE_TEXT_WITH_DISABLED_STATUS:
      return (
        <ClickableTextWithDisabledStatusCell
          value={props.value}
          handleClickableTextWithDisabledStatusClick={
            props.handleClickableTextWithDisabledStatusClick
          }
        />
      );
    case TableCellType.CHECKBOX:
      return (
        <CheckboxCell
          value={props.value}
          toggleCheckboxSelection={props.toggleCheckboxSelection}
        />
      );
    case TableCellType.EDITABLE_TEXT:
      return (
        <EditableTextCellComponent
          value={props.value}
          onValueChange={props.onValueChange ?? (() => {})}
          header={props.header}
          rowIndex={props.rowIndex}
          onTableRowUpdate={props.onTableRowUpdate}
        />
      );
    case TableCellType.SELECTABLE_BADGE:
      return (
        <SelectableBadgeCell
          value={props.value}
          rowIndex={props.rowIndex}
          header={props.header}
          onValueChange={props.onValueChange}
          onTableRowUpdate={props.onTableRowUpdate}
        />
      );
    case TableCellType.SEARCHABLE_CARD:
      return (
        <SearchableCardCell
          value={props.value}
          rowIndex={props.rowIndex}
          header={props.header}
          onValueChange={props.onValueChange}
          onTableRowUpdate={props.onTableRowUpdate}
          onSearchChange={props.onSearchChange}
          onCreateOption={props.onCreateOption}
          onOptionClick={props.onOptionClick}
        />
      );
    case TableCellType.THINKING_BUTTON:
      return <ThinkingButtonCell value={props.value} />;
    case TableCellType.RPA_STATUS:
      return (
        <RpaStatusCell
          value={props.value}
          propagateEventOnClickInRpaStatusCell={
            props.onPropagateEventOnClickInRpaStatusCell
          }
        />
      );
    case TableCellType.VIEW_ICON:
      return <ViewIconCell value={props.value} />;
    case TableCellType.DOC_NAME_WITH_ICON:
      return <DocNameWithIcon docDetails={props.value} />;
    case TableCellType.STRING_WITH_BUTTON:
      return <StringWithButtonCell value={props.value} />;

    case TableCellType.TAG_WITH_ICON:
      return <TagWithIconCell value={props.value} />;

    case TableCellType.RPA_STATUS_FOR_EXTERNAL:
      return (
        <RpaStatusForExternalCell
          value={props.value}
          onRpaStatusForExternalClick={props.onRpaStatusForExternalClick}
        />
      );

    case TableCellType.DOC_LIST:
      return <DocListCell docList={props.value} />;
    case TableCellType.STRING_WITH_CUSTOM_TOOLTIP:
      return <StringWithCustomToolTipCell value={props.value} />;
    case TableCellType.VIEW_ICON_FOR_SCREENSHOT_OR_API_RESPONSE:
      return <ViewIconForScreenshotOrApiResponse value={props.value} />;

    case TableCellType.SIDE_MODAL_ICON:
      return (
        <SideModalIconCell
          value={props.value}
          onSideModalClose={props.onSideModalClose}
        />
      );

    case TableCellType.TEXT_CELL_WITH_INPUT:
      return (
        <TextCellWithInput
          value={props.value}
          onValueChangeOfTextField={props.onValueChangeOfTextField}
        />
      );

    case TableCellType.RPA_STATUS_TWO:
      const shiftMasterQueueStatusForWhichModalShouldOpen = [
        MedicalPaOrdersAuthStatus.Pending,
        MedicalPaOrdersAuthStatus.AuthRequired,
        MedicalPaOrdersAuthStatus.Query,
        MedicalPaOrdersAuthStatus.Hold,
      ];

      const getColorBasedOnStatus = () => {
        switch (props.value.status) {
          case DocWriteBackStatus.SUCCESS:
            return STATUS_COLORS.SUCCESS.stroke;
          case DocWriteBackStatus.ERROR:
            return STATUS_COLORS.ERROR.stroke;
          case DocWriteBackStatus.IN_PROGRESS:
          case DocWriteBackStatus.QUEUED:
            return STATUS_COLORS.IN_PROGRESS.stroke;
          default:
            return STATUS_COLORS.DEFAULT.stroke;
        }
      };

      const getBackgroundColorBasedOnStatus = () => {
        switch (props.value.status) {
          case DocWriteBackStatus.SUCCESS:
            return STATUS_COLORS.SUCCESS.background;
          case DocWriteBackStatus.ERROR:
            return STATUS_COLORS.ERROR.background;
          case DocWriteBackStatus.IN_PROGRESS:
          case DocWriteBackStatus.QUEUED:
            return STATUS_COLORS.IN_PROGRESS.background;
          default:
            return STATUS_COLORS.DEFAULT.background;
        }
      };

      const getStatusTextTwo = () => {
        switch (props.value.status) {
          case DocWriteBackStatus.SUCCESS:
            return "Doc(s) are added";
          case DocWriteBackStatus.ERROR:
            return "Failed to add doc(s)";
          case DocWriteBackStatus.IN_PROGRESS:
          case DocWriteBackStatus.QUEUED:
            return "Doc(s) are being added";
          default:
            return "Doc(s) not initiated";
        }
      };

      const shouldPropagateEventOrOpenModal = () => {
        if (props.value.shouldPropagate === true) {
          props.onPropagateEventOnClickInRpaStatusTwoCell?.(props.value.id);
        } else {
          dispatch(
            setOpenedModalId({
              id: ModalId.IMAGE_VIEWER_MODAL,
              metaData: { filePaths: props.value.files },
            }),
          );
        }
      };

      return (
        <div
          className="table-cell-rpa-status ml-auto h-fit w-fit"
          onMouseEnter={() => setIsCommentModalOpen(true)}
          onMouseLeave={() => setIsCommentModalOpen(false)}
          ref={commentModalRef}
          onClick={(e) => {
            e.stopPropagation();
            if (
              shiftMasterQueueStatusForWhichModalShouldOpen.includes(
                props.value.masterAuthStatus,
              ) &&
              props.value.status === DocWriteBackStatus.NOT_INITIATED
            ) {
              shouldPropagateEventOrOpenModal();
            } else if (props.value.status === DocWriteBackStatus.SUCCESS) {
              shouldPropagateEventOrOpenModal();
            }
          }}
        >
          <DocumentUploadIconWithBgColor
            fillColor={getBackgroundColorBasedOnStatus()}
            strokeColor={getColorBasedOnStatus()}
            height={"24px"}
            width={"24px"}
          />

          <ToolTipUsingPortal
            children={getStatusTextTwo()}
            placement={"left"}
            showTooltip={isCommentModalOpen}
            parentRef={commentModalRef}
          />
        </div>
      );

    case TableCellType.DOC_WITH_UPLOAD_ICON:
      return (
        <DocWithUploadIconCell
          value={props.value}
          handleIconClickOnDocumentScreenshot={
            props.handleIconClickOnDocumentScreenshot
          }
        />
      );

    case TableCellType.EDIT_DELETE_ICONS:
      return <EditDeleteIconsCell value={props.value} />;

    case TableCellType.BADGE_GROUP:
      return <BadgeGroupCell value={props.value} />;
    case TableCellType.OVERLAPPING_ICONS_WITH_POPOVER_AND_ACTIONS:
      return (
        <OverlappingIconsWithPopoverAndActionsCell
          value={props.value}
          onOverlappingIconClick={props.onOverlappingIconClick}
        />
      );
    case TableCellType.IS_DOCUMENT_UPLOADED:
      return (
        <IsDocumentUploaded
          isUploaded={props.value.isUploaded}
          isVisible={props.value.isVisible}
          onUploadClick={props.onUploadClick}
          onSuccessClick={props.onSuccessClick}
          orderId={props.value.orderId ?? ""}
          jCode={props.value.jCode ?? ""}
          jCodeId={props.value.jCodeId ?? ""}
          authStatus={props.value.authStatus ?? ""}
          authLetterReviewStatus={props.value.authLetterReviewStatus}
        />
      );
    default:
      return <></>;
  }
};

TableCell.propTypes = {
  type: PropTypes.string.isRequired,
  id: PropTypes.string,
  value: PropTypes.any.isRequired,
  header: PropTypes.string,
  rowIndex: PropTypes.number,
  globalRowIndex: PropTypes.number,
  rowData: PropTypes.any,
  onReviewClick: PropTypes.func,
  handleAssignToClick: PropTypes.func,
  handleStatusClick: PropTypes.func,
  handleDeleteIconClick: PropTypes.func,
  handleEditIconClick: PropTypes.func,
  handleIconCellClick: PropTypes.func,
  handleClickableTextWithDisabledStatusClick: PropTypes.func,
  onComplete: PropTypes.func,
  onClickableBadgeClick: PropTypes.func,
  toggleCheckboxSelection: PropTypes.func,
  handleDownloadButtonClick: PropTypes.func,
  onTableRowUpdate: PropTypes.func,
  onValueChange: PropTypes.func,
  onPropagateEventOnClickInRpaStatusCell: PropTypes.func,
  onPropagateEventOnClickInRpaStatusTwoCell: PropTypes.func,
  onSideModalClose: PropTypes.func,
  onChangeEmit: PropTypes.func,
  onValueChangeOfTextField: PropTypes.func,
  onOverlappingIconClick: PropTypes.func,
  onRpaStatusForExternalClick: PropTypes.func,
  onUploadClick: PropTypes.func,
  onSuccessClick: PropTypes.func,
  handleIconClickOnDocumentScreenshot: PropTypes.func,
  onSearchChange: PropTypes.func,
  onCreateOption: PropTypes.func,
  onOptionClick: PropTypes.func,
};
